// src/admin/AdminJourneyCities.js
import React, { useState, useEffect } from 'react';
import CityPreview from './CityPreview';

/* Util: filename from Firebase URL */
const getFileName = (url) => {
  try {
    const decoded = decodeURIComponent(url);
    const match = decoded.match(/\/([^/?#]+)\?/);   // after last slash
    return match ? match[1] : 'file';
  } catch {
    return 'file';
  }
};

/* Image integrity checker for existing URLs */
const ImageValidator = ({ url }) => {
  const [ok, setOk] = useState(true);
  const [load, setLoad] = useState(true);
  useEffect(() => {
    const img = new Image();
    img.onload = () => { setOk(true); setLoad(false); };
    img.onerror = () => { setOk(false); setLoad(false); };
    img.src = url;
  }, [url]);
  if (load) return <span>Checking…</span>;
  return ok ? null : (
    <span title="Image might be broken" style={{ color: 'red', marginRight: 6 }}>
      ⚠️
    </span>
  );
};

function AdminJourneyCities({
  cities,
  selectedCity,
  countries,
  handleCityInputChange,
  handleEditCity,
  handleDeleteCity,
  handleSaveCityChanges,
  handleUploadCityImage,
  handleDeleteCityImage,
  handleUploadCoverImage,
  handleDeleteCoverImage,
  setSelectedCity,
}) {
  /* ─────────── imageList mirrors Hostels page ─────────── */
  const [imageList, setImageList] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  /* refresh imageList when we pick another city */
  useEffect(() => {
    if (!selectedCity?.id) { setImageList([]); return; }
    const existing = (selectedCity.images || []).map((url) => ({
      isNew: false,
      url,
      name: getFileName(url),
      file: null,
      selected: true,
    }));
    setImageList(existing);
  }, [selectedCity]);

  /* ─────── file selection & manipulation helpers ─────── */
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setImageList((p) => [
      ...p,
      ...files.map((file) => ({
        isNew: true,
        file,
        url: null,
        name: file.name,
        selected: true,
      })),
    ]);
  };
  const removeNew = (i) => setImageList((p) => p.filter((_, idx) => idx !== i));
  const toggleSelect = (i) =>
    setImageList((p) =>
      p.map((img, idx) => (idx === i ? { ...img, selected: !img.selected } : img))
    );
  const move = (i, dir) =>
    setImageList((p) => {
      const arr = [...p];
      const tgt = dir === 'up' ? i - 1 : i + 1;
      if (tgt < 0 || tgt >= arr.length) return p;
      [arr[i], arr[tgt]] = [arr[tgt], arr[i]];
      return arr;
    });
  const removeExisting = async (cityId, url, i) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await handleDeleteCityImage(cityId, url);
      setImageList((p) => p.filter((_, idx) => idx !== i));
    } catch (err) {
      alert(`Failed to delete: ${err.message}`);
    }
  };

  /* Save button orchestrates upload then calls parent save */
  const saveAll = async () => {
    setUploadErrors([]);

    if (!selectedCity.name) {
      alert('City Name is required');
      return;
    }

    /* 1) upload newly‑added files */
    const newImgs = imageList.filter((img) => img.isNew && img.selected);
    const uploadedURLs = [];

    // Note: If adding a NEW city with images, the following image upload
    // will likely fail because selectedCity.id is not yet available
    // to the handleUploadCityImage function from the parent.
    // The city record itself will be created, but without these new images.
    for (const img of newImgs) {
      try {
        // handleUploadCityImage (from AdminCitiesPage) expects selectedCity.id to be set
        // in AdminCitiesPage's state. For a brand new city, this ID doesn't exist yet
        // at this stage of the saveAll function.
        const url = await handleUploadCityImage(img.file);
        uploadedURLs.push({ name: img.name, url });
      } catch (err) {
        setUploadErrors((prev) => [
          ...prev,
          `Upload failed ${img.name}: ${err.message}`,
        ]);
      }
    }

    /* 2) combine & dedupe */
    let final = imageList.map((img) =>
      img.isNew
        ? uploadedURLs.find((u) => u.name === img.name)?.url || null
        : img.url
    );
    final = final.filter(Boolean); // Remove nulls (e.g., from failed uploads)

    /* 3) update selectedCity & persist */
    // For a new city, selectedCity.id is still empty here.
    // The handleSaveCityChanges function in AdminCitiesPage will handle
    // creating a new document if data.id is missing.
    const updated = { ...selectedCity, images: final };
    handleEditCity(updated); // Updates local state for AdminJourneyCities
    await handleSaveCityChanges(updated); // This will call addDoc in the parent for new cities

    alert('City processed! ' + (uploadErrors.length ? `(${uploadErrors.join(', ')})` : ''));
    // Note: handleSaveCityChanges in the parent component resets the selectedCity form.
  };


  return (
    <div style={{ marginBottom: 40 }}>
      <h3>Manage Cities</h3>

      {/* ─── CITY FORM ─── */}
      <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0 }}>City Details</h3>
        <label style={{ display: 'block', marginBottom: '10px' }}>
          City Name:
          <input
            name="name"
            type="text"
            value={selectedCity.name || ''}
            onChange={handleCityInputChange}
            style={{ marginLeft: '10px', padding: '5px', width: '200px' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '10px' }}>
          Country:
          <select
            name="countryCode"
            value={selectedCity.countryCode || ''}
            onChange={handleCityInputChange}
            style={{ marginLeft: '10px', padding: '5px', width: '210px' }}
          >
            <option value="">Select a country</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'block', marginBottom: '10px' }}>
          IATA Code:
          <input
            name="iataCode"
            type="text"
            value={selectedCity.iataCode || ''}
            onChange={handleCityInputChange}
            style={{ marginLeft: '10px', padding: '5px', width: '100px' }}
          />
        </label>


        <label style={{ display: 'block', marginBottom: '10px' }}>
          Notes:
          <input
            name="notes"
            type="text"
            value={selectedCity.notes || ''}
            onChange={handleCityInputChange}
            style={{ marginLeft: '10px', padding: '5px', width: '300px' }}
          />
        </label>

        <div style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}>
          <label>
            Latitude:
            <input
              name="latitude"
              type="number"
              step="0.000001"
              value={selectedCity.latitude || ''}
              onChange={handleCityInputChange}
              style={{ marginLeft: '10px', padding: '5px', width: '150px' }}
            />
          </label>

          <label>
            Longitude:
            <input
              name="longitude"
              type="number"
              step="0.000001"
              value={selectedCity.longitude || ''}
              onChange={handleCityInputChange}
              style={{ marginLeft: '10px', padding: '5px', width: '150px' }}
            />
          </label>
        </div>

        <label style={{ display: 'block', marginTop: '15px' }}>
          City Description:
          <textarea
            name="description"
            value={selectedCity.description || ''}
            onChange={handleCityInputChange}
            rows="4"
            style={{ width: '100%', marginTop: '5px' }}
            placeholder="Enter a detailed description of the city..."
          />
        </label>

        <label style={{ display: 'block', marginTop: '15px' }}>
          City Vibes:
          <select
            name="cityVibes"
            value={selectedCity.cityVibes || ''}
            onChange={handleCityInputChange}
            style={{ marginLeft: '10px', padding: '5px', width: '150px' }}
          >
            <option value="">Select vibes</option>
            <option value="Mountains">Mountains</option>
            <option value="Beach">Beach</option>
            <option value="Urban">Urban</option>
          </select>
        </label>

        {/* Manual Content Sections */}
        <h4 style={{ marginTop: '25px', marginBottom: '15px', color: '#853a38' }}>City Content Sections</h4>

        <label style={{ display: 'block', marginBottom: '15px' }}>
          Attractions:
          <textarea
            name="attractions"
            value={selectedCity.attractions || ''}
            onChange={handleCityInputChange}
            rows="3"
            style={{ width: '100%', marginTop: '5px' }}
            placeholder="List attractions, activities, and things to do..."
          />
        </label>

        <label style={{ display: 'block', marginBottom: '15px' }}>
          Food & Dining:
          <textarea
            name="food"
            value={selectedCity.food || ''}
            onChange={handleCityInputChange}
            rows="3"
            style={{ width: '100%', marginTop: '5px' }}
            placeholder="Describe local cuisine, restaurants, and food experiences..."
          />
        </label>

        <label style={{ display: 'block', marginBottom: '15px' }}>
          Transportation:
          <textarea
            name="transportation"
            value={selectedCity.transportation || ''}
            onChange={handleCityInputChange}
            rows="3"
            style={{ width: '100%', marginTop: '5px' }}
            placeholder="Explain how to get around the city, public transport options..."
          />
        </label>

        <label style={{ display: 'block', marginBottom: '15px' }}>
          Services:
          <textarea
            name="services"
            value={selectedCity.services || ''}
            onChange={handleCityInputChange}
            rows="3"
            style={{ width: '100%', marginTop: '5px' }}
            placeholder="List essential services like laundry, ATMs, medical facilities..."
          />
        </label>

        <label style={{ display: 'block', marginBottom: '15px' }}>
          Testimonials:
          <textarea
            name="testimonials"
            value={selectedCity.testimonials || ''}
            onChange={handleCityInputChange}
            rows="3"
            style={{ width: '100%', marginTop: '5px' }}
            placeholder="Add traveler testimonials and experiences..."
          />
        </label>
      </div>

      {/* ─── COVER IMAGE SECTION ─── */}
      <div style={{ margin: '20px 0', padding: '15px', border: '2px solid #853a38', borderRadius: '5px' }}>
        <h4>Cover Picture</h4>
        {selectedCity.coverImage ? (
          <div style={{ marginBottom: '10px' }}>
            <img
              src={selectedCity.coverImage}
              alt="Cover"
              style={{ width: '200px', height: '150px', objectFit: 'cover', borderRadius: '5px' }}
            />
            <button
              style={{
                marginLeft: '10px',
                background: '#c00',
                color: '#fff',
                padding: '5px 10px',
                border: 'none',
                cursor: 'pointer'
              }}
              onClick={() => {
                if (window.confirm('Delete cover image?')) {
                  handleDeleteCoverImage(selectedCity.id);
                }
              }}
            >
              Delete Cover
            </button>
          </div>
        ) : (
          <p style={{ color: 'gray' }}>No cover image set</p>
        )}

        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            if (e.target.files[0]) {
              setCoverImageFile(e.target.files[0]);
            }
          }}
        />

        {coverImageFile && (
          <div style={{ marginTop: '10px' }}>
            <p>Selected: {coverImageFile.name}</p>
            <button
              style={{
                background: '#853a38',
                color: '#fff',
                padding: '8px 14px',
                border: 'none',
                marginRight: '10px',
                cursor: 'pointer'
              }}
              onClick={async () => {
                if (!selectedCity.id) {
                  alert('Please save the city first before uploading a cover image');
                  return;
                }
                setUploadingCover(true);
                try {
                  const url = await handleUploadCoverImage(coverImageFile);
                  const updated = { ...selectedCity, coverImage: url };
                  setSelectedCity(updated);
                  await handleSaveCityChanges(updated);
                  setCoverImageFile(null);
                  alert('Cover image uploaded successfully');
                } catch (err) {
                  alert(`Cover upload failed: ${err.message}`);
                } finally {
                  setUploadingCover(false);
                }
              }}
              disabled={uploadingCover}
            >
              {uploadingCover ? 'Uploading...' : 'Upload Cover'}
            </button>
            <button
              style={{
                background: '#999',
                color: '#fff',
                padding: '8px 14px',
                border: 'none',
                cursor: 'pointer'
              }}
              onClick={() => setCoverImageFile(null)}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* ─── IMAGES SECTION ─── */}
      <div style={{ margin: '20px 0' }}>
        <h4>City Gallery Images</h4>
        <p>
          Total (existing + new): <strong>{imageList.length}</strong>
        </p>

        {uploadErrors.length > 0 && (
          <div style={{ color: 'red' }}>
            {uploadErrors.map((e, i) => (
              <p key={i}>{e}</p>
            ))}
          </div>
        )}

        <input type="file" multiple accept="image/*" onChange={handleFileChange} />

        <div
          style={{
            border: '1px solid #ddd',
            padding: 8,
            marginTop: 10,
          }}
        >
          <h5>Preview / Manage (current order):</h5>
          {imageList.length === 0 ? (
            <p style={{ color: 'gray' }}>No images yet.</p>
          ) : (
            imageList.map((img, i) => {
              const isNew = img.isNew;
              const url = isNew ? URL.createObjectURL(img.file) : img.url;
              return (
                <div
                  key={`${img.name}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 6,
                  }}
                >
                  {/* reorder */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <button onClick={() => move(i, 'up')} style={{ cursor: 'pointer' }}>↑</button>
                    <button onClick={() => move(i, 'down')} style={{ cursor: 'pointer' }}>↓</button>
                  </div>

                  {/* image */}
                  {isNew ? (
                    <img src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover' }} />
                  ) : (
                    <>
                      <ImageValidator url={url} />
                      <img src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover' }} />
                    </>
                  )}

                  {/* filename */}
                  <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {img.name}
                  </span>

                  {/* actions */}
                  {!isNew ? (
                    <button
                      style={{ background: '#c00', color: '#fff', cursor: 'pointer' }}
                      onClick={() => removeExisting(selectedCity.id, img.url, i)}
                    >
                      Delete
                    </button>
                  ) : (
                    <>
                      <button
                        style={{ background: '#c00', color: '#fff', cursor: 'pointer' }}
                        onClick={() => removeNew(i)}
                      >
                        Remove
                      </button>
                      <label>
                        <input
                          type="checkbox"
                          checked={img.selected}
                          onChange={() => toggleSelect(i)}
                        />{' '}
                        Upload
                      </label>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* SAVE BUTTON */}
        <button
          onClick={saveAll}
          style={{
            marginTop: 10,
            background: '#853a38',
            color: '#fff',
            padding: '8px 14px',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {selectedCity.id ? 'Save City' : 'Add City'}
        </button>
      </div>

      {selectedCity.id && (
        <button
          style={{
            background: '#853a38',
            color: '#fff',
            padding: '8px 14px',
            border: 'none',
            marginBottom: 20,
            cursor: 'pointer',
          }}
          onClick={() => setShowPreview(true)}
        >
          Preview with Current Images
        </button>
      )}

      {showPreview && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: 20,
              borderRadius: 8,
              maxWidth: 400,
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              textAlign: 'center',
            }}
          >
            <h3>Preview: {selectedCity.name}</h3>
            <CityPreview
              cityName={selectedCity.name}
              notes={selectedCity.notes}
              existingImages={imageList
                .map((img) =>
                  img.isNew && img.selected ? URL.createObjectURL(img.file) : img.url
                )
                .filter(Boolean)}
            />
            <button
              style={{ marginTop: 20, background: '#853a38', color: '#fff', padding: '6px 12px', cursor: 'pointer' }}
              onClick={() => setShowPreview(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ─── CITY TABLE ─── */}
      <h4>Current Cities</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f0f0f0' }}>
            <th style={{ padding: '8px', border: '1px solid #ddd' }}>Name</th>
            <th style={{ padding: '8px', border: '1px solid #ddd' }}>Country</th>
            <th style={{ padding: '8px', border: '1px solid #ddd' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {cities.map((c) => (
            <tr key={c.id}>
              <td style={{ padding: '8px', border: '1px solid #ddd' }}>{c.name}</td>
              <td style={{ padding: '8px', border: '1px solid #ddd' }}>{c.country || ''}</td>
              <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                <button onClick={() => handleEditCity(c)} style={{ cursor: 'pointer' }}>Edit</button>{' '}
                <button onClick={() => handleDeleteCity(c.id)} style={{ cursor: 'pointer' }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminJourneyCities;